/**
 * Service for extracting and formatting user display names
 */

export interface UserProfile {
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  sub?: string;
  email?: string;
}

/**
 * Extracts first name from email address
 * @param email - Email address to extract name from
 * @returns Formatted first name
 */
const extractFirstNameFromEmail = (email: string): string => {
  if (!email || !email.includes('@')) {
    return email || 'User';
  }
  
  const localPart = email.split('@')[0];
  const firstName = localPart.split('.')[0].split('_')[0].split('-')[0];
  
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
};

/**
 * Extracts first name from full name string
 * @param fullName - Full name string
 * @returns First name only
 */
const extractFirstNameFromFullName = (fullName: string): string => {
  if (!fullName || typeof fullName !== 'string') {
    return 'User';
  }
  
  const trimmedName = fullName.trim();
  const firstName = trimmedName.split(' ')[0];
  
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
};

/**
 * Formats a name string with proper capitalization
 * @param name - Name to format
 * @returns Properly formatted name
 */
const formatName = (name: string): string => {
  if (!name || typeof name !== 'string') {
    return 'User';
  }
  
  const trimmedName = name.trim();
  if (!trimmedName) {
    return 'User';
  }
  
  return trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1).toLowerCase();
};

/**
 * Gets user's first name from profile with comprehensive fallback logic
 * @param profile - User profile object
 * @returns User's first name
 */
export const getUserDisplayName = (profile: UserProfile | null | undefined): string => {
  if (!profile) {
    return 'User';
  }

  // Priority 1: given_name (most reliable for first name)
  if (profile.given_name) {
    return formatName(profile.given_name);
  }

  // Priority 2: Extract first name from full name
  if (profile.name) {
    return extractFirstNameFromFullName(profile.name);
  }

  // Priority 3: Extract from email
  if (profile.email) {
    return extractFirstNameFromEmail(profile.email);
  }

  // Priority 4: Extract from preferred_username
  if (profile.preferred_username) {
    // Check if it looks like an email
    if (profile.preferred_username.includes('@')) {
      return extractFirstNameFromEmail(profile.preferred_username);
    }
    return formatName(profile.preferred_username);
  }

  // Priority 5: Extract from sub (subject identifier)
  if (profile.sub) {
    // Check if it looks like an email
    if (profile.sub.includes('@')) {
      return extractFirstNameFromEmail(profile.sub);
    }
    return formatName(profile.sub);
  }

  return 'User';
};

/**
 * Gets user role indicator for display
 * @param roles - Array of user roles
 * @returns Role indicator string or empty string
 */
export const getUserRoleIndicator = (roles: string[] = []): string => {
  const isAdmin = roles.some(role => 
    role?.toLowerCase().includes('admin') || 
    role === 'FlightRangeAdmin'
  );
  
  return isAdmin ? ' (Admin)' : '';
};

/**
 * User Display Service - exported functions for external use
 */
export const UserDisplayService = {
  getUserDisplayName,
  getUserRoleIndicator,
  extractFirstNameFromEmail,
  extractFirstNameFromFullName,
  formatName
} as const;